import React, { useState, useEffect } from 'react';

import { sendGTMEvent } from '@/utils/analytics';
import { formatSavedAtJp } from '@/utils/date';

import { LOCAL_STORAGE_KEY_TACTICALBOARD_V2 } from '@/constants';
import { useTacticalboardDrawer } from '@/contexts/TacticalboardDrawerContext';
import TacticalboardDrawer from '@/components/tacticalboard/TacticalboardDrawer';

interface TacticalBoardSave {
  id: string;
  title: string;
  savedAt: string;
  image: string;
  data: {
    objects: unknown[];
    drawnElements: unknown[];
    player1Settings: { players: unknown[] };
    player2Settings: { players: unknown[] };
    player3Settings: { players: unknown[] };
    fieldType: string;
    isRotationMode: boolean;
  };
}

interface MyBoardsModal2Props {
  closeModal: () => void;
  onSelectImage: (data: string) => void;
}

const MyBoardsModal2: React.FC<MyBoardsModal2Props> = ({ closeModal, onSelectImage }) => {
  const { openDrawer, closeDrawer } = useTacticalboardDrawer();

  // ローカルストレージから取得したデータを格納するための状態
  const [tacticalBoardSaves, setTacticalBoardSaves] = useState<TacticalBoardSave[]>([]);

  /**
   * ローカルストレージから戦術ボード保存データを取得
   */
  const fetchDataFromLocalStorage = () => {
    try {
      const savedData = localStorage.getItem(LOCAL_STORAGE_KEY_TACTICALBOARD_V2);
      if (savedData) {
        const parsedData: TacticalBoardSave[] = JSON.parse(savedData);

        // savedAtで降順ソート（新しい順）
        const sortedData = parsedData.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());

        setTacticalBoardSaves(sortedData);
      } else {
        setTacticalBoardSaves([]);
      }
    } catch (error) {
      console.error('Failed to parse tacticalBoardSaves from localStorage:', error);
      setTacticalBoardSaves([]);
    }
  };

  /**
   * 戦術ボードを選択した時の処理
   */
  const handleSelectBoard = (imageData: string) => {
    onSelectImage(imageData);
    closeModal();
  };

  /**
   * ドロワー表示（サッカーボード）
   */
  const handleOpenDrawer = () => {
    sendGTMEvent('openTacticalboardv2');
    openDrawer(<TacticalboardDrawer onCloseDrawer={closeDrawer} />);
  };

  useEffect(() => {
    // 初回マウント時にデータを取得
    fetchDataFromLocalStorage();

    // 3秒ごとにデータを最新化する処理をセットアップ
    const intervalId = setInterval(fetchDataFromLocalStorage, 3000);

    // コンポーネントがアンマウントされるときにクリーンアップ
    return () => clearInterval(intervalId);
  }, []);

  /**
   * モーダルの背景クリック時の処理
   */
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // モーダルの背景をクリックした場合にだけ closeModal を呼び出す
    const modalBackground = document.getElementById('modal-background');
    if (e.target === modalBackground) {
      closeModal();
    }
  };

  return (
    <div
      id="modal-background"
      className="fixed inset-0 z-30 w-full flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
      onClick={handleModalClick}>
      <div className="relative w-[90%] h-[90%] mx-auto my-6">
        <div className="relative flex flex-col bg-white h-full overflow-y-auto border-0 rounded-lg outline-none focus:outline-none">
          <div className="flex justify-between items-center border-b border-green-500 px-4 py-2 bg-white sticky top-0 z-10 rounded-t-lg">
            <h3 className="font-footballlog italic text-lg font-semibold text-gray-500 dark:text-white">
              My Boards<span className="text-sm"> - サッカーボードを選択</span>
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close menu</span>
            </button>
          </div>
          <div className="relative p-4">
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <li className="flex flex-col w-full justify-between min-h-48 bg-gray-100 rounded-lg">
                <ul className="h-full list text-gray-500 text-xs p-4">
                  <li className="mb-2 flex items-top space-x-3 rtl:space-x-reverse">
                    <svg
                      className="mt-1 flex-shrink-0 w-3 h-3 text-green-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 16 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 5.917 5.724 10.5 15 1.5"
                      />
                    </svg>
                    <span>ノート（orコメント）に添付するサッカーボードを選択してください。</span>
                  </li>
                  <li className="mb-2 flex items-top space-x-3 rtl:space-x-reverse">
                    <svg
                      className="mt-1 flex-shrink-0 w-3 h-3 text-green-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 16 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 5.917 5.724 10.5 15 1.5"
                      />
                    </svg>
                    <span>サッカーボードアプリで、事前のスケッチとMy Boardsへの保存が必要です。</span>
                  </li>
                  <li className="flex items-top space-x-3 rtl:space-x-reverse">
                    <svg
                      className="mt-1 flex-shrink-0 w-3 h-3 text-green-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 16 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 5.917 5.724 10.5 15 1.5"
                      />
                    </svg>
                    <span>
                      上手くいった・失敗したプレーの状況をサッカーボードにスケッチすることで、プレーの再現性や課題意識を高めることができ効果的です。
                    </span>
                  </li>
                </ul>
              </li>
              <li className="grid grid-cols-1 w-full h-48 rounded-lg">
                <button
                  className="flex flex-col justify-center items-center hover:bg-gray-50 w-full h-full rounded-lg border border-gray-300 bg-cover bg-center text-green-500"
                  type="button"
                  onClick={handleOpenDrawer}>
                  <div className="text-lg font-semibold text-gray-400">新しいプレーシーン</div>
                  <svg
                    className="w-8 h-8 text-gray-400 dark:text-white"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      fillRule="evenodd"
                      d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4.243a1 1 0 1 0-2 0V11H7.757a1 1 0 1 0 0 2H11v3.243a1 1 0 1 0 2 0V13h3.243a1 1 0 1 0 0-2H13V7.757Z"
                    />
                  </svg>
                </button>
              </li>
              {tacticalBoardSaves.map((save) => (
                <li
                  key={save.id}
                  className="flex flex-col justify-center rounded-lg w-full h-48 bg-gray-200 bg-cover bg-center relative">
                  <img
                    src={save.image}
                    alt={save.title}
                    style={{
                      objectFit: 'contain',
                      height: '100%'
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gray-900/60 rounded-b-lg text-white text-xs p-2">
                    <div>{formatSavedAtJp(save.savedAt)}</div>
                  </div>
                  <button
                    type="button"
                    className="flex justify-center items-center absolute top-0 right-0 w-10 h-10 shadow rounded-full bg-green-500 hover:bg-green-600"
                    onClick={() => handleSelectBoard(save.image)}>
                    <svg
                      width="32px"
                      height="32px"
                      viewBox="0 0 40 40"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg">
                      <g id="check" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <rect
                          id="Rectangle"
                          stroke="#FFFFFF"
                          strokeWidth="3"
                          x="10.5"
                          y="10.5"
                          width="19"
                          height="19"
                          rx="4"></rect>
                        <polyline
                          id="Path-2"
                          stroke="#FFFFFF"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          points="12.6040733 18.1639234 18.2686046 24.2779102 32.6040733 10.2779102"></polyline>
                      </g>
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBoardsModal2;
